import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Loader2, Check, X, Flag } from "lucide-react";

export default function AdminTrustTab() {
  const [data, setData] = useState(null);
  const [mod, setMod] = useState({ events: [], flagged_users: [] });
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/noshow/admin/cases").then((r) => setData(r.data)).catch(() => setData({ cases: [], outstanding_fees: [] }));
    api.get("/noshow/admin/moderation").then((r) => setMod(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const act = async (fn, ok, key) => {
    setBusy(key);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-trust">
      <div>
        <h3 className="font-display font-semibold">Trust &amp; safety</h3>
        <p className="text-sm text-muted-foreground mt-0.5">No-show cases, outstanding fees and private moderation history.</p>
      </div>

      <div>
        <h4 className="text-sm font-semibold flex items-center gap-1.5"><ShieldAlert className="h-4 w-4 text-accent" /> No-show cases</h4>
        {data.cases.length === 0 ? <p className="text-sm text-muted-foreground mt-2">No no-show reports.</p> : (
          <div className="space-y-3 mt-3">
            {data.cases.map((c) => (
              <div key={c.case_id} className="bg-card rounded-2xl border border-primary/5 p-4" data-testid={`admin-case-${c.case_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-sm">{c.cleaner_name} · {c.service_name} · {c.date}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{c.status}</span>
                  <span className="ml-auto text-xs text-muted-foreground">reported {new Date(c.reported_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Customer: {c.customer_name}{c.customer_details ? ` — "${c.customer_details}"` : ""}</p>
                {c.responses?.length > 0 && <p className="text-sm mt-1">Last response: {c.responses[c.responses.length - 1].label}</p>}
                {c.disputes?.length > 0 && <p className="text-sm mt-1 italic">Dispute: {c.disputes[c.disputes.length - 1].explanation}</p>}
                {!["confirmed", "dismissed"].includes(c.status) && (
                  <div className="flex gap-2 mt-3">
                    <button disabled={!!busy} onClick={() => act(() => api.post(`/noshow/admin/${c.case_id}/resolve`, { outcome: "confirm" }), "No-show confirmed", c.case_id)}
                      className="px-4 py-1.5 rounded-full bg-red-600 text-white text-xs font-medium inline-flex items-center gap-1" data-testid={`confirm-case-${c.case_id}`}>
                      <Check className="h-3.5 w-3.5" /> Confirm no-show
                    </button>
                    <button disabled={!!busy} onClick={() => act(() => api.post(`/noshow/admin/${c.case_id}/resolve`, { outcome: "dismiss" }), "Report dismissed", c.case_id)}
                      className="px-4 py-1.5 rounded-full border border-border text-xs font-medium inline-flex items-center gap-1" data-testid={`dismiss-case-${c.case_id}`}>
                      <X className="h-3.5 w-3.5" /> Dismiss
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {data.outstanding_fees?.length > 0 && (
        <div data-testid="admin-fees">
          <h4 className="text-sm font-semibold">Outstanding cleaner fees</h4>
          <div className="space-y-2 mt-3">
            {data.outstanding_fees.map((f) => (
              <div key={f.fee_id} className="bg-card rounded-xl border border-primary/5 p-3 flex flex-wrap items-center gap-2">
                <p className="text-sm">£{f.amount.toFixed(2)} · {f.type.replace("_", " ")} · cleaner {f.cleaner_id}</p>
                <button disabled={!!busy} onClick={() => act(() => api.post(`/noshow/admin/fees/${f.fee_id}/settle`), "Fee marked as paid", f.fee_id)}
                  className="ml-auto px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium" data-testid={`settle-fee-${f.fee_id}`}>
                  Mark as paid
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold flex items-center gap-1.5"><Flag className="h-4 w-4 text-accent" /> Contact-sharing moderation (private)</h4>
        {mod.flagged_users?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="flagged-users">
            {mod.flagged_users.map((u) => (
              <span key={u.user_id} className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs">
                {u.name} · {u.moderation_strikes} strike{u.moderation_strikes === 1 ? "" : "s"}
                {u.messaging_restricted ? " · restricted" : ""}{u.suspension_recommended ? " · suspension recommended" : ""}
              </span>
            ))}
          </div>
        )}
        {mod.events?.length === 0 ? <p className="text-sm text-muted-foreground mt-2">No moderation events.</p> : (
          <div className="space-y-2 mt-3">
            {mod.events.slice(0, 25).map((e) => (
              <div key={e.event_id} className="bg-card rounded-xl border border-primary/5 p-3" data-testid={`mod-event-${e.event_id}`}>
                <p className="text-sm font-medium">{e.user_email} · {e.user_role} {e.strike ? "· strike recorded" : "· first warning"}</p>
                <p className="text-sm text-muted-foreground mt-0.5">Detected: {e.categories?.join(", ")}</p>
                <p className="text-xs text-muted-foreground mt-1 italic">"{e.excerpt}"</p>
                <p className="text-xs text-muted-foreground mt-1">{new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
